import { PureComponent } from 'react';

import { onlineCoursePracticeQuestionAttempted } from 'my-core/gtm-events';
import { checkSolution, hasRevealableSolution } from 'my-core/quiz-utils';
import { debounce } from 'my-utils';

import ActivityQuestions from 'my-components/ActivityQuestions';
import ActivityVideoPlayer from 'my-components/ActivityVideoPlayer';
import { MultiColumnContentsWrapper } from 'my-components/OnlineCourse';
import GatedDialog from 'my-components/OnlineCourse/InCourseExperience/GatedDialog';
import QuestionLayout from 'my-components/QuestionLayout';
import QuizQuestion, { LoadingFallback } from 'my-components/QuizQuestion';

import PracticeQuestionActions from './PracticeQuestionActions';

import Skeleton from '@mui/material/Skeleton';

export default class PracticeQuestion extends PureComponent {
  state = {
    gatedMessageOpen: false,
    showResults: !!(this.props.submission?.is_submitted || this.props.reviewMode),
    solutionRevealed: false,
  };

  componentDidMount() {
    this.reportViewing();
  }

  componentDidUpdate(prevProps) {
    this.reportViewing();
    const { canCheckSolution, fetching, tokenPurchaseStatus } = this.props;
    if (tokenPurchaseStatus !== prevProps.tokenPurchaseStatus) {
      if (tokenPurchaseStatus.success) {
        if (canCheckSolution && !this.state.showResults) {
          this.checkSolution();
        }
      } else if (tokenPurchaseStatus.error) {
        this.setState({ gatedMessageOpen: true });
      }
    }
    if (!fetching && prevProps.fetching) {
      this.setState(({ showResults }) => ({
        showResults: !!(showResults || this.props.submission?.is_submitted || this.props.reviewMode),
      }));
    }
  }

  componentWillUnmount() {
    this.props.onViewingChange?.(null);
  }

  getSubmission() {
    const { submission } = this.props;
    return this.state.draftSubmission || submission?.current_submission;
  }

  reportViewing() {
    const { onViewingChange, quizQuestion } = this.props;
    if (!onViewingChange) return;
    if (!quizQuestion) return onViewingChange(null);

    const subQuestionId =
      quizQuestion.type === 'MultiPartQuestion' ? quizQuestion.sub_questions?.[this.activePartIndex()]?.id : undefined;

    return onViewingChange({
      sub_question_id: subQuestionId,
      position_secs: this.solutionPlayheadSecs,
      submission: this.getSubmission(),
    });
  }

  handleSolutionProgress = ({ playedSeconds }) => {
    this.solutionPlayheadSecs = Math.round(playedSeconds);
    this.reportViewing();
  };

  // Resolves once an explicit save has reached the server. The autosave branch resolves straight
  // away: it is debounced, so there is nothing yet to wait on.
  updateSubmission = (submission, qqId, params) =>
    new Promise(resolve => {
      this.setState({ draftSubmission: submission }, () => {
        if (params) {
          this.debouncedSaveSubmission.cancel();
          resolve(this.saveSubmission(params));
        } else {
          if (this.props.autoSave) this.debouncedSaveSubmission();
          resolve();
        }
      });
    });

  saveSubmission = params => {
    const { onSaveSubmission } = this.props;
    const { draftSubmission } = this.state;
    return draftSubmission && onSaveSubmission(draftSubmission, params);
  };
  debouncedSaveSubmission = debounce(this.saveSubmission, 1000);

  checkSolution = () => {
    const {
      canCheckSolution,
      canTokenPurchase,
      eventTrackingProperties,
      onCompleted,
      onTokenPurchase,
      quizQuestion,
      submission,
    } = this.props;
    const currentSubmission = this.getSubmission();
    checkSolution(quizQuestion, currentSubmission.response, currentSubmission.result).then(result => {
      if (canCheckSolution || !canTokenPurchase)
        onlineCoursePracticeQuestionAttempted(
          canCheckSolution,
          result,
          currentSubmission.response === null,
          Object.keys(submission?.submission_attempts || {}).length + 1,
          eventTrackingProperties,
        );
      if (canCheckSolution) {
        // Results paint from `result` above rather than from the save, so nothing here waits on
        // the network — but `onCompleted` does. Listeners take it as the cue to read this
        // question's score back out, and until the attempt lands there is no score to read: the
        // study session was counting a question it had just been told was finished, and could
        // not yet see an answer for, as one the student got right.
        this.updateSubmission({ ...currentSubmission, result }, quizQuestion.id, { new_attempt: true }).then(
          err => !err && onCompleted?.(),
        );
        this.setState({ showResults: true });
      } else {
        // Nothing was saved, so there is nothing to wait for. Still reported finished: a student
        // who hits the paywall has engaged with the activity, which is what callers count here.
        onCompleted?.();
        if (canTokenPurchase) {
          onTokenPurchase();
        } else {
          this.setState({ gatedMessageOpen: true });
        }
      }
    });
  };

  // The part on screen: the one they picked since a link last named a part, else that link's.
  activePartIndex() {
    const { defaultSubQuestionId, quizQuestion, subQuestionLinkKey } = this.props;
    const { pickedPart } = this.state;
    if (pickedPart && pickedPart.linkKey === subQuestionLinkKey) return pickedPart.index;
    return Math.max(quizQuestion?.sub_questions?.findIndex(sq => sq.id === defaultSubQuestionId) ?? 0, 0);
  }

  changeActiveSubQuestionIndex = index =>
    this.setState({ pickedPart: { index, linkKey: this.props.subQuestionLinkKey } });
  toggleSolution = () => this.setState(({ solutionRevealed }) => ({ solutionRevealed: !solutionRevealed }));
  retryQuestion = () => {
    const { onResetSubmission } = this.props;
    onResetSubmission();
    this.setState({ draftSubmission: {}, showResults: false, solutionRevealed: false });
  };

  render() {
    const {
      assignmentId,
      canCheckSolution,
      canTokenPurchase,
      courseId,
      courseSubscription,
      eventTrackingProperties,
      fetching,
      hideQuestionForm,
      instructionMode,
      onlineCourse,
      onlineCourseId,
      onlineCourseUser,
      // onStartTrial,
      onPurchase,
      onSaveProgress,
      onShowFeedback,
      onTogglePlaying,
      onTokenPurchase,
      onViewingChange,
      playing,
      practiceModeSectionId,
      progress,
      quizQuestion: question,
      quizQuestionSkipDisabled,
      quizQuestionType,
      reviewMode,
      secondaryCTAProps,
      slotProps,
      slots,
      submission: submissionProp,
      subQuestionLinkKey,
      // subscriptionTrialStatus,
      subscription,
      tokenPurchaseStatus,
      userCourse,
      videoDrmId,
    } = this.props;
    const { gatedMessageOpen, showResults, solutionRevealed } = this.state;
    const mpqPartIndex = this.activePartIndex();
    const submission = this.getSubmission();

    const questionType = question?.type || quizQuestionType;
    // Deriving both the reveal and its control from this keeps a revealed solution from outliving
    // the button to hide it when the active part changes.
    const activePartHasSolution = hasRevealableSolution(question, question?.sub_questions?.[mpqPartIndex]);
    const showSolution = solutionRevealed && activePartHasSolution;
    const canToggleSolution = !!instructionMode && !showResults && activePartHasSolution;

    const Gate = slots?.gate || GatedDialog;

    const questionComponent = (
      <QuizQuestion
        // Its part tabs only read the part to open on as they mount.
        key={subQuestionLinkKey}
        defaultPartIndex={mpqPartIndex}
        disableContent
        fallback={<LoadingFallback questionType={questionType} />}
        fetching={fetching}
        onChangeActiveSubQuestionIndex={this.changeActiveSubQuestionIndex}
        onCheckSubmission={this.checkSolution}
        onSaveSubmission={this.updateSubmission}
        onUpdateSubmission={this.updateSubmission}
        question={question}
        questionType={questionType} // for loading state
        resultsAttemptTimestamp="current"
        showResults={showResults}
        skipDisabled={quizQuestionSkipDisabled}
        submission={submission}
      />
    );
    const actions = (
      <PracticeQuestionActions
        onCheckSolution={this.checkSolution}
        onRetryQuestion={!!submissionProp?.id && this.retryQuestion}
        onShowFeedback={onShowFeedback}
        onToggleSolution={canToggleSolution && this.toggleSolution}
        question={question}
        secondaryCTAProps={secondaryCTAProps}
        showResults={showResults}
        solutionRevealed={showSolution}
        submission={submission}
      />
    );

    const askAQuestionComponent = question && (onlineCourse || onlineCourseId) && showResults && (
      <ActivityQuestions
        activity={question}
        assignmentId={assignmentId}
        courseId={courseId}
        enableTour
        eventTrackingProperties={eventTrackingProperties}
        hideQuestionForm={hideQuestionForm}
        onlineCourse={onlineCourse}
        onlineCourseId={onlineCourseId}
        onPurchase={onPurchase}
        practiceModeSectionId={practiceModeSectionId}
        subQuestionId={question.type === 'MultiPartQuestion' ? question.sub_questions?.[mpqPartIndex]?.id : undefined}
        subQuestions={question.sub_questions}
        subscription={subscription}
      />
    );

    return (
      <>
        <QuestionLayout
          actions={actions}
          askAQuestion={askAQuestionComponent}
          eventTrackingProperties={eventTrackingProperties}
          fetching={fetching}
          onUpdateSubmission={this.updateSubmission}
          partIndex={questionType === 'MultiPartQuestion' ? mpqPartIndex : undefined}
          question={question}
          questionComponent={questionComponent}
          questionType={questionType}
          showResults={showResults}
          showSolution={showSolution}
          submission={submission}
          video={
            !!question?.video && (
              <ActivityVideoPlayer
                elements={question?.video_elements}
                eventTrackingProperties={eventTrackingProperties}
                onProgress={onViewingChange ? this.handleSolutionProgress : undefined}
                onSaveProgress={onSaveProgress}
                onTogglePlaying={onTogglePlaying}
                playing={playing}
                progress={progress}
                reviewMode={reviewMode}
                video={question?.video}
                videoDrmId={videoDrmId}
              />
            )
          }
          {...slotProps?.questionLayout}
        />
        {!canCheckSolution && !canTokenPurchase && (
          <Gate
            actionText="check your answer and get a step-by-step solution"
            activityText="Solution"
            courseSubscription={courseSubscription}
            eventTrackingProperties={eventTrackingProperties}
            onClose={() => this.setState({ gatedMessageOpen: false })}
            onlineCourse={onlineCourse}
            onlineCourseUser={onlineCourseUser}
            onPurchase={onPurchase}
            onTokenPurchase={onTokenPurchase}
            open={gatedMessageOpen}
            packageMode={practiceModeSectionId ? 'practice' : undefined}
            subscription={subscription}
            tokenPurchaseStatus={tokenPurchaseStatus}
            userCourse={userCourse}
            // onStartTrial={onStartTrial}
            // subscriptionTrialStatus={subscriptionTrialStatus}
          />
        )}
      </>
    );
  }
}

const skeletonCss = ({ breakpoints, palette, shape, spacing }) => ({
  '.PQ-paper': {
    padding: spacing(2),
    backgroundColor: palette.background.paper,
    border: `1px solid ${palette.divider}`,
    borderRadius: shape.borderRadius,
    [breakpoints.up('sm')]: { padding: spacing(4) },
  },
  '.PQ-stem': { display: 'flex', flexDirection: 'column', gap: spacing(0.5), margin: spacing(2, 0) },
  '.PQ-answers': { display: 'flex', flexDirection: 'column', gap: spacing(1) },
  '.PQ-answer': {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1.5),
    padding: spacing(1.5),
    border: `1px solid ${palette.divider}`,
    borderRadius: shape.borderRadiusSm,
  },
  '.PQ-answerText': { flex: 1, minWidth: 0 },
  '.PQ-actions': { display: 'flex', justifyContent: 'flex-end', marginTop: spacing(4) },
});

export function PracticeQuestionSkeleton({ className }) {
  return (
    <MultiColumnContentsWrapper className={className} css={skeletonCss}>
      <div>
        <div className="PQ-paper">
          <Skeleton width="45%" />
          <div className="PQ-stem">
            <Skeleton />
            <Skeleton width="88%" />
            <Skeleton width="64%" />
          </div>
        </div>
      </div>
      <div>
        <div className="PQ-paper">
          <Skeleton width="30%" />
          <div className="PQ-stem">
            <Skeleton />
            <Skeleton width="70%" />
          </div>
          <div className="PQ-answers">
            {[0, 1, 2, 3].map(idx => (
              <div key={idx} className="PQ-answer">
                <Skeleton height={20} variant="circular" width={20} />
                <div className="PQ-answerText">
                  <Skeleton width={`${[54, 86, 63, 78][idx]}%`} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="PQ-actions">
          <Skeleton height={36} variant="rounded" width={160} />
        </div>
      </div>
    </MultiColumnContentsWrapper>
  );
}
